import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Calendar as CalendarIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { formatCurrency, formatDate } from '../../lib/utils';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfitLoss = () => {
  const [profitLoss, setProfitLoss] = useState(null);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfitLoss();
  }, [startDate, endDate]);

  const fetchProfitLoss = async () => {
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/finance/profit-loss?start_date=${startStr}&end_date=${endStr}`);
      setProfitLoss(response.data);
    } catch (error) {
      console.error('Failed to fetch profit/loss:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded animate-pulse w-48" />
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profit-loss-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Profit & Loss Statement</h1>
          <p className="text-slate-600 mt-1">Income statement for the selected period</p>
        </div>
        <div className="flex gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="start-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                From: {format(startDate, 'PP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="end-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                To: {format(endDate, 'PP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Revenue</p>
              <h3 className="text-2xl font-bold text-emerald-600 font-mono">
                {formatCurrency(profitLoss?.revenue.total || 0)}
              </h3>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Expenses</p>
              <h3 className="text-2xl font-bold text-red-600 font-mono">
                {formatCurrency(profitLoss?.expenses.total || 0)}
              </h3>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              (profitLoss?.net_income || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'
            }`}>
              <FileText className={`w-6 h-6 ${
                (profitLoss?.net_income || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Net Income</p>
              <h3 className={`text-2xl font-bold font-mono ${
                (profitLoss?.net_income || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {formatCurrency(profitLoss?.net_income || 0)}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {profitLoss && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-slate-900">ACADEMION SCHOOL</h2>
            <p className="text-slate-600">Profit & Loss Statement</p>
            <p className="text-sm text-slate-500">
              For the period {formatDate(profitLoss.period.start_date)} to {formatDate(profitLoss.period.end_date)}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Revenue */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-900 border-b-2 border-emerald-500 pb-2 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                REVENUE
              </h3>
              <div className="space-y-2 pl-4">
                {profitLoss.revenue.items.length > 0 ? (
                  profitLoss.revenue.items.map((item, index) => (
                    <div key={index} className="flex justify-between py-1">
                      <span className="text-slate-700">{item.code} - {item.name}</span>
                      <span className="font-mono text-emerald-600">{formatCurrency(item.balance)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">No revenue recorded for this period</p>
                )}
              </div>
              <div className="flex justify-between py-2 mt-4 border-t border-slate-300 font-semibold">
                <span>Total Revenue</span>
                <span className="font-mono text-emerald-600">{formatCurrency(profitLoss.revenue.total)}</span>
              </div>
            </div>

            {/* Expenses */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-900 border-b-2 border-red-500 pb-2 mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                EXPENSES
              </h3>
              <div className="space-y-2 pl-4">
                {profitLoss.expenses.items.length > 0 ? (
                  profitLoss.expenses.items.map((item, index) => (
                    <div key={index} className="flex justify-between py-1">
                      <span className="text-slate-700">{item.code} - {item.name}</span>
                      <span className="font-mono text-red-600">{formatCurrency(item.balance)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">No expenses recorded for this period</p>
                )}
              </div>
              <div className="flex justify-between py-2 mt-4 border-t border-slate-300 font-semibold">
                <span>Total Expenses</span>
                <span className="font-mono text-red-600">{formatCurrency(profitLoss.expenses.total)}</span>
              </div>
            </div>

            {/* Net Income */}
            <div className={`p-4 rounded-lg ${
              profitLoss.net_income >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-slate-900">NET INCOME</span>
                <span className={`text-2xl font-bold font-mono ${
                  profitLoss.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {formatCurrency(profitLoss.net_income)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLoss;
